/**
 * Server-side recovery: status-aware reattach.
 *
 * When the SSE stream drops (proxy timeout, network hiccup, tab sleep,
 * or a container redeploy on Railway), the agent EITHER keeps running
 * server-side OR is dead. The previous implementation polled
 * `/sessions/:id/activity` every 3 s for up to 30 minutes hoping the
 * activity feed would report `complete: true` — which means a 30-minute
 * spinner if the run had actually died, with no way for the UI to tell.
 *
 * The new flow uses two endpoints:
 *
 *   1. `GET /sessions/:id/run-status` returns the lifecycle status of
 *      the latest run (running | completed | failed | killed) plus a
 *      heartbeat timestamp. Tells us *immediately* whether to wait,
 *      hydrate, or surface an error.
 *
 *   2. `GET /chat/runs/:run_id/stream?since=<idx>` reattaches an SSE
 *      stream to an in-flight run by replaying activity events from
 *      Neo4j and tailing until terminal status is reached.
 *
 * Recovery is bounded by the `REATTACH_TIMEOUT_MS` ceiling instead of
 * the old 30-minute poll cap — once we know the run died, we surface
 * that straight away.
 *
 * Pure module: no React, no store. The caller threads in callbacks
 * (`setMessages`, `pushActivity`) so this file stays trivially testable
 * and reusable from any chat surface that needs the same recovery.
 */

import { env } from "@/env";
import type { ChatMessage } from "@models/chat";
import type { ActivityEntry } from "@stores/chatStore";
import { normalizeMessages } from "@utils/mermaidExtract";
import { sessionsApi } from "../services/api";
import { readSseEvents } from "./chatSseStream";

// Wall-clock ceiling — matches the agent's turn budget so the worst case
// is the same whether the SSE stayed up or we reattached.
const REATTACH_TIMEOUT_MS = 60 * 60_000;
// No-progress watchdog — backend tails activity every 1.5 s and writes
// SSE pings every 20 s, so 60 s of complete silence means the wire is
// genuinely wedged. Same threshold as the live `sendMessage` watchdog
// in useChat.ts so both surfaces fail in the same window.
const REATTACH_IDLE_TIMEOUT_MS = 60_000;

type SetMessages = (
  msgs: ChatMessage[],
  pagination?: { hasMoreOlder?: boolean; oldestTimestamp?: number | null },
) => void;

interface RecoveryOptions {
  sessionId: string;
  capturedRunId: string | null;
  setMessages: SetMessages;
  pushActivity: (entry: ActivityEntry) => void;
  /**
   * Optional: when reattach replays activity events from Neo4j,
   * artifacts on those tool_results get surfaced live to the
   * StreamingMessage view (same code path the live SSE uses). Omit
   * if the caller doesn't want live-artifact rendering during
   * reattach (e.g. headless tests).
   */
  pushLiveArtifact?: (artifact: import("@models/chat").ToolArtifact) => void;
  stopSignal: { current: boolean };
}

export interface RecoveryResult {
  recovered: boolean;
  errorMessage?: string;
}

/**
 * Top-level recovery entry: figure out what happened to the run and
 * either hydrate, reattach, or surface the error.
 */
export async function recoverFromStreamDrop(
  opts: RecoveryOptions,
): Promise<RecoveryResult> {
  const {
    sessionId,
    capturedRunId,
    setMessages,
    pushActivity,
    pushLiveArtifact,
    stopSignal,
  } = opts;

  // Step 1 — what's the deal with the run? One round-trip tells us
  // whether to give up, hydrate, or reattach.
  let status: Awaited<ReturnType<typeof sessionsApi.getRunStatus>>;
  try {
    status = await sessionsApi.getRunStatus(sessionId);
  } catch {
    return { recovered: false };
  }
  if (stopSignal.current) return { recovered: false };

  // No run row at all (legacy session pre-deploy, or a quota-refused
  // turn). Fall back to a single activity check for `complete: true`.
  if (status === null) {
    try {
      const activity = await sessionsApi.getActivity(sessionId);
      if (activity.complete) {
        const page = await sessionsApi.getMessages(sessionId);
        setMessages(normalizeMessages(page.messages), {
          hasMoreOlder: page.has_more,
          oldestTimestamp: page.oldest_timestamp,
        });
        return { recovered: true };
      }
    } catch {
      // ignore
    }
    return { recovered: false };
  }

  // Step 2 — terminal statuses are easy. Hydrate or surface the error.
  if (status.status === "completed") {
    try {
      const page = await sessionsApi.getMessages(sessionId);
      setMessages(normalizeMessages(page.messages), {
        hasMoreOlder: page.has_more,
        oldestTimestamp: page.oldest_timestamp,
      });
      return { recovered: true };
    } catch {
      return { recovered: false };
    }
  }
  if (status.status === "failed" || status.status === "killed") {
    const fallback =
      status.status === "killed"
        ? "The server restarted while the assistant was working. " +
          "Your messages are saved — please retry."
        : "The agent encountered an error.";
    return {
      recovered: false,
      errorMessage: status.error_summary || fallback,
    };
  }

  // Step 3 — `running`. Reattach via SSE if we know the run_id.
  const runId = capturedRunId ?? status.run_id;
  if (!runId) return { recovered: false };

  // Baseline at the tail of the activity feed so the reattach replay
  // doesn't push duplicates of events the user already saw during the
  // pre-drop streaming phase.
  let sinceIndex = -1;
  try {
    const activity = await sessionsApi.getActivity(sessionId);
    const last = activity.entries.at(-1);
    if (last) sinceIndex = last.index;
  } catch {
    // Live with `since=-1` (full replay of activity entries).
  }
  if (stopSignal.current) return { recovered: false };

  return _consumeReattachStream({
    sessionId,
    runId,
    sinceIndex,
    setMessages,
    pushActivity,
    pushLiveArtifact,
    stopSignal,
  });
}

interface ReattachOptions {
  sessionId: string;
  runId: string;
  sinceIndex: number;
  setMessages: SetMessages;
  pushActivity: (entry: ActivityEntry) => void;
  pushLiveArtifact?: (artifact: import("@models/chat").ToolArtifact) => void;
  stopSignal: { current: boolean };
}

async function _consumeReattachStream(
  opts: ReattachOptions,
): Promise<RecoveryResult> {
  const {
    sessionId,
    runId,
    sinceIndex,
    setMessages,
    pushActivity,
    pushLiveArtifact,
    stopSignal,
  } = opts;

  const url =
    `${env.VITE_API_BASE_URL}/chat/runs/${encodeURIComponent(runId)}/stream` +
    `?since=${encodeURIComponent(String(sinceIndex))}`;

  const ac = new AbortController();
  // Surface the stop signal into the reattach fetch so a click on Stop
  // tears down the SSE connection promptly instead of letting it tick
  // through the full timeout.
  const stopWatcher = setInterval(() => {
    if (stopSignal.current) {
      ac.abort();
      clearInterval(stopWatcher);
    }
  }, 100);
  // Hard ceiling — match the agent's own turn budget so the worst case
  // is the same whether the SSE stayed up or we reattached.
  const timeoutId = setTimeout(() => ac.abort(), REATTACH_TIMEOUT_MS);

  // No-progress watchdog. Reset on every reader.read() resolution
  // (including SSE comment heartbeats the parser drops) so the
  // watchdog only trips on a genuinely wedged wire.
  let idleAborted = false;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const resetIdle = () => {
    if (idleTimer !== null) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      idleAborted = true;
      ac.abort();
    }, REATTACH_IDLE_TIMEOUT_MS);
  };
  const clearIdle = () => {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };

  let errorMessage: string | undefined;
  let doneSeen = false;

  try {
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "text/event-stream" },
      signal: ac.signal,
    });
    if (!response.ok || !response.body) return { recovered: false };

    const reader = response.body.getReader();
    resetIdle();
    for await (const ev of readSseEvents(reader, {
      stopSignal,
      onProgress: resetIdle,
    })) {
      if (ev.type === "done") {
        doneSeen = true;
        break;
      }
      if (ev.type === "error") {
        errorMessage = ev.content || "The agent run failed.";
        break;
      }
      _projectActivityEvent(ev, pushActivity, pushLiveArtifact);
    }
  } catch {
    return { recovered: false };
  } finally {
    clearInterval(stopWatcher);
    clearTimeout(timeoutId);
    clearIdle();
  }

  if (errorMessage) {
    return { recovered: false, errorMessage };
  }
  if (idleAborted) {
    return {
      recovered: false,
      errorMessage:
        "The connection stalled while reattaching — no response from the " +
        "agent for a minute. Your messages are saved — please retry.",
    };
  }
  if (!doneSeen) {
    return { recovered: false };
  }
  // The reattach handler emits `done` only when the run reaches the
  // `completed` terminal state, so it's safe to hydrate the final
  // message here.
  try {
    const page = await sessionsApi.getMessages(sessionId);
    setMessages(normalizeMessages(page.messages), {
      hasMoreOlder: page.has_more,
      oldestTimestamp: page.oldest_timestamp,
    });
    return { recovered: true };
  } catch {
    return { recovered: false };
  }
}

/**
 * Translate one SSE activity event (thinking / tool_call / tool_result)
 * into a `pushActivity` call. Shared between the reattach path and the
 * live stream so both surfaces handle the same payload shapes the
 * same way.
 */
export function _projectActivityEvent(
  ev: { type?: string; content?: string },
  pushActivity: (entry: ActivityEntry) => void,
  /**
   * Optional live-artifact pusher. When provided, artifacts arriving
   * on `tool_result` SSE events are surfaced immediately to the
   * StreamingMessage view so charts / schematics render the moment
   * the agent emits them — instead of waiting for the post-`done`
   * message hydration. The reattach path passes the same pusher so
   * a viewer who refreshes mid-turn still sees the artifacts as
   * they're replayed by the backend.
   */
  pushLiveArtifact?: (artifact: import("@models/chat").ToolArtifact) => void,
): void {
  if (ev.type === "thinking") {
    pushActivity({ kind: "thinking", label: ev.content ?? "" });
    return;
  }
  if (ev.type === "tool_call" && ev.content) {
    try {
      const tc = JSON.parse(ev.content) as {
        name?: string;
        args?: Record<string, unknown>;
      };
      if (tc.name) {
        pushActivity({ kind: "tool_call", label: tc.name, args: tc.args });
        return;
      }
    } catch {
      // fall through to raw-string render
    }
    pushActivity({ kind: "tool_call", label: ev.content });
    return;
  }
  if (ev.type === "tool_result" && ev.content) {
    try {
      const tr = JSON.parse(ev.content) as {
        name?: string;
        summary?: string;
        artifacts?: Array<{
          kind?: string;
          url?: string;
          label?: string;
          content_type?: string;
        }>;
      };
      if (tr.name) {
        pushActivity({
          kind: "tool_result",
          label: tr.name,
          summary: tr.summary || undefined,
        });
        // Live-artifact surfacing — see signature comment above.
        if (pushLiveArtifact && Array.isArray(tr.artifacts)) {
          for (const a of tr.artifacts) {
            if (!a?.url) continue;
            pushLiveArtifact({
              kind: (a.kind as "image" | "link" | string) || "link",
              url: a.url,
              label: a.label,
              content_type: a.content_type,
            });
          }
        }
        return;
      }
    } catch {
      // fall through to raw-string render
    }
    pushActivity({ kind: "tool_result", label: ev.content });
  }
}
